// /src/components/ExcelViewer/ExcelViewer.tsx
"use client";
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './ExcelViewer.module.css'; // Adjust to .css if not using modules

interface ExcelViewerProps {
  filePath: string;
}

// Define a type for Excel data (adjust based on your data structure)
interface ExcelRow {
  [key: string]: string | number | boolean | null | undefined;
}

const ExcelViewer: React.FC<ExcelViewerProps> = ({ filePath }) => {
  const [data, setData] = useState<ExcelRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadExcel = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Failed to fetch Excel file: ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: ExcelRow[] = XLSX.utils.sheet_to_json(sheet);
        setData(jsonData);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (filePath) {
      loadExcel();
    } else {
      setError('No file path provided');
      setLoading(false);
    }
  }, [filePath]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (data.length === 0) return <div>No data found in the Excel file.</div>;

  return (
    <div>
      <table>
        <thead>
          <tr>
            {Object.keys(data[0]).map((key) => (
              <th key={key}>{key}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              {Object.values(row).map((value, i) => (
                <td key={i}>{value != null ? String(value) : ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExcelViewer;
